import { useRef } from 'react'
import DatePickerLib from 'react-datepicker'
import { Calendar } from 'lucide-react'
import { cn } from '@lib/utils/helpers'
import { validateDateOfBirth, getMinDateOfBirth, getMaxDateOfBirth, parseLocalDateString } from '@lib/utils/dateValidation'
import 'react-datepicker/dist/react-datepicker.css'
import './DatePicker.css'
import {
  PORTALED_DATEPICKER_POPPER_MODIFIERS,
  PORTALED_DATEPICKER_POPPER_PROPS,
} from './DatePicker.constants'
import type { DatePickerProps } from './DatePicker.types'

/**
 * Parse API / form values into a local calendar Date, or null if empty/invalid.
 * Handles `YYYY-MM-DD` and ISO strings like `YYYY-MM-DDTHH:mm:ss` (timezone-safe for the date part).
 */
function parseValueToLocalDate(value: string): Date | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  const m = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!m) return null
  const year = Number(m[1])
  const month = Number(m[2])
  const day = Number(m[3])
  if (![year, month, day].every((n) => Number.isFinite(n))) return null
  const d = new Date(year, month - 1, day)
  if (Number.isNaN(d.getTime())) return null
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return null
  return d
}

export function DatePicker({
  value,
  onChange,
  onBlur,
  disabled = false,
  readOnly = false,
  placeholder = 'Select date',
  className,
  error,
  showHelperText = true,
  minDate,
  maxDate,
  allowFutureDates = false,
  id,
  name,
  autoComplete = 'off',
  dataTestId,
  calendarButtonDataTestId,
  errorDataTestId,
  helperTextDataTestId,
  popperPlacement: placement = 'bottom-start',
  popperFixed = false,
  calendarPortalId,
  filterDate,
}: DatePickerProps) {
  const fieldWrapperRef = useRef<HTMLDivElement>(null)

  const nonInteractive = disabled || readOnly

  const openPickerFromIcon = () => {
    if (nonInteractive) return
    const input = fieldWrapperRef.current?.querySelector<HTMLInputElement>(
      '.react-datepicker__input-container input',
    )
    input?.focus()
    input?.click()
  }

  const selectedDate = parseValueToLocalDate(typeof value === 'string' ? value : '')

  // Parse min/max as local dates (new Date('YYYY-MM-DD') would parse as UTC and shift the day)
  const minDateValue = (minDate || parseLocalDateString(getMinDateOfBirth())) ?? new Date(0)
  const maxDateValue = allowFutureDates && !maxDate ? undefined : ((maxDate || parseLocalDateString(getMaxDateOfBirth())) ?? new Date())

  const resolvedCalendarButtonTestId =
    calendarButtonDataTestId ?? (dataTestId ? `${dataTestId}-calendar-button` : undefined)
  const resolvedErrorTestId = errorDataTestId ?? (dataTestId ? `${dataTestId}-error` : undefined)
  const resolvedHelperTestId = helperTextDataTestId ?? (dataTestId ? `${dataTestId}-helper` : undefined)

  const usePortaledCalendar = Boolean(calendarPortalId)
  const popperClassName = usePortaledCalendar
    ? 'date-picker-popper date-picker-popper--portaled'
    : 'date-picker-popper'

  const datePickerRef = useRef<DatePickerLib | null>(null)

  const handleDateSelect = () => {
    window.setTimeout(() => {
      datePickerRef.current?.setOpen(false)
    }, 0)
  }

  const handleDateChange = (date: Date | null) => {
    if (date) {
      // Format date as YYYY-MM-DD
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const formattedDate = `${year}-${month}-${day}`

      // Only validate if not allowing future dates
      if (!allowFutureDates) {
        const validation = validateDateOfBirth(formattedDate)
        if (validation.isValid) {
          onChange(formattedDate)
        } else {
          // Still update the value but show error
          onChange(formattedDate)
        }
      } else {
        // Allow any date (future dates allowed)
        onChange(formattedDate)
      }
      handleDateSelect()
    } else {
      onChange('')
    }
  }

  return (
    <div className="relative" data-testid={dataTestId}>
      <div
        ref={fieldWrapperRef}
        className={cn('relative', error && 'date-picker-field--error')}
      >
        <DatePickerLib
          ref={datePickerRef}
          selected={selectedDate}
          onSelect={handleDateSelect}
          onChange={handleDateChange}
          onBlur={onBlur}
          disabled={nonInteractive}
          readOnly={readOnly}
          minDate={minDateValue}
          maxDate={maxDateValue}
          dateFormat="MM/dd/yyyy"
          placeholderText={placeholder}
          shouldCloseOnSelect={true}
          allowSameDay
          fixedHeight
          className={cn(
            'w-full',
            disabled && 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-500',
            readOnly && !disabled && 'cursor-default bg-white text-slate-700',
            className
          )}
          wrapperClassName="w-full"
          id={id}
          name={name}
          autoComplete={autoComplete}
          showYearDropdown
          showMonthDropdown
          dropdownMode="select"
          yearDropdownItemNumber={120}
          scrollableYearDropdown
          popperClassName={popperClassName}
          popperPlacement={placement}
          {...(usePortaledCalendar
            ? {
                portalId: calendarPortalId,
                popperProps: PORTALED_DATEPICKER_POPPER_PROPS,
                popperModifiers: PORTALED_DATEPICKER_POPPER_MODIFIERS,
              }
            : popperFixed
              ? { popperProps: { strategy: 'fixed' as const } }
              : {})}
          disabledKeyboardNavigation
          {...(filterDate ? { filterDate } : {})}
          onChangeRaw={(e) => {
            if (!e) return

            // Only format if the change was triggered by user input (typing)
            // This prevents interfering with calendar selections
            if (!(e.nativeEvent instanceof InputEvent)) {
              return
            }

            const event = e.nativeEvent as InputEvent
            if (event.inputType === 'deleteContentBackward' || event.inputType === 'deleteContentForward') {
              return
            }

            const target = e.target as HTMLInputElement
            const rawValue = target.value

            const digits = rawValue.replace(/\D/g, '')
            let formatted = digits

            if (digits.length >= 5) {
              formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`
            } else if (digits.length >= 3) {
              formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`
            }

            if (formatted !== rawValue) {
              target.value = formatted
            }
          }}
        />
        <button
          type="button"
          tabIndex={-1}
          disabled={nonInteractive}
          data-testid={resolvedCalendarButtonTestId}
          aria-label="Open calendar"
          className={cn(
            'absolute right-3 top-1/2 z-10 -translate-y-1/2 border-0 bg-transparent p-0',
            nonInteractive
              ? readOnly && !disabled
                ? 'cursor-default opacity-100'
                : 'cursor-not-allowed opacity-50'
              : 'cursor-pointer',
          )}
          onMouseDown={(e) => {
            e.preventDefault()
            openPickerFromIcon()
          }}
        >
          <Calendar className="h-5 w-5 text-gray-400" />
        </button>
      </div>
      {error && (
        <span
          role="alert"
          className="mt-1 block text-sm text-red-600"
          data-testid={resolvedErrorTestId}
        >
          {error}
        </span>
      )}
      {showHelperText && !error && !allowFutureDates && (
        <p className="mt-1 text-xs text-gray-500" data-testid={resolvedHelperTestId}>
          Date must be in the past and not more than 110 years ago
        </p>
      )}
    </div>
  )
}

